"use client";

import type React from "react";
import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  Flex,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  agentService,
  DEFAULT_RETELL_TEXT_MODEL,
} from "../../services/agentService.ts";
import { useAppTheme } from "../../ThemeContext.tsx";

// Icons for each agent type
// const ConversationFlowIcon = () => (
//   <Box
//     as="span"
//     w="24px"
//     h="24px"
//     borderRadius="full"
//     bg="gray.700"
//     display="flex"
//     alignItems="center"
//     justifyContent="center"
//     mr={3}
//   >
//     <svg
//       width="16"
//       height="16"
//       viewBox="0 0 24 24"
//       fill="none"
//       xmlns="http://www.w3.org/2000/svg"
//     >
//       <path
//         d="M8 2L2 8L8 14"
//         stroke="white"
//         strokeWidth="2"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//       />
//       <path
//         d="M2 8H16C19.3137 8 22 10.6863 22 14V22"
//         stroke="white"
//         strokeWidth="2"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//       />
//     </svg>
//   </Box>
// );

const SinglePromptIcon = ({ isDark }) => (
  <Box
    as="span"
    w="40px"
    h="40px"
    borderRadius="full"
    color={isDark ? "white" : "black"}
    bg={isDark ? "gray.700" : "gray.100"}
    shadow="md"
    display="flex"
    alignItems="center"
    justifyContent="center"
    mr={3}
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="brackets-fill">
        <path
          id="Vector"
          d="M7.75 3.25V4.75H5.5V15.25H7.75V16.75H4V3.25H7.75ZM12.25 3.25H16V16.75H12.25V15.25H14.5V4.75H12.25V3.25Z"
          fill="currentColor"
        ></path>
      </g>
    </svg>
  </Box>
);

const MultiPromptIcon = ({ isDark }) => (
  <Box
    as="span"
    w="40px"
    h="40px"
    borderRadius="full"
    color={isDark ? "white" : "black"}
    bg={isDark ? "gray.700" : "gray.100"}
    shadow="md"
    display="flex"
    alignItems="center"
    justifyContent="center"
    mr={3}
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="organization-chart">
        <path
          id="Vector"
          d="M12.25 3.25C12.664 3.25 13 3.586 13 4V7C13 7.414 12.664 7.75 12.25 7.75H10.75V9.25H13.75C14.164 9.25 14.5 9.586 14.5 10V12.25H16C16.414 12.25 16.75 12.586 16.75 13V16C16.75 16.414 16.414 16.75 16 16.75H11.5C11.086 16.75 10.75 16.414 10.75 16V13C10.75 12.586 11.086 12.25 11.5 12.25H13V10.75H7V12.25H8.5C8.914 12.25 9.25 12.586 9.25 13V16C9.25 16.414 8.914 16.75 8.5 16.75H4C3.586 16.75 3.25 16.414 3.25 16V13C3.25 12.586 3.586 12.25 4 12.25H5.5V10C5.5 9.586 5.836 9.25 6.25 9.25H9.25V7.75H7.75C7.336 7.75 7 7.414 7 7V4C7 3.586 7.336 3.25 7.75 3.25H12.25ZM7.75 13.75H4.75V15.25H7.75V13.75ZM15.25 13.75H12.25V15.25H15.25V13.75ZM11.5 4.75H8.5V6.25H11.5V4.75Z"
          fill="currentColor"
        ></path>
      </g>
    </svg>
  </Box>
);

const CustomLLMIcon = ({ isDark }) => (
  <Box
    as="span"
    w="40px"
    h="40px"
    borderRadius="full"
    color={isDark ? "white" : "black"}
    bg={isDark ? "gray.700" : "gray.100"}
    shadow="md"
    display="flex"
    alignItems="center"
    justifyContent="center"
    mr={3}
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="links-line">
        <path
          id="Vector"
          d="M10.7949 7.08249L11.8562 8.14374C12.3438 8.63126 12.7305 9.21004 12.9944 9.84703C13.2583 10.484 13.3941 11.1668 13.3941 11.8562C13.3941 12.5457 13.2583 13.2285 12.9944 13.8655C12.7305 14.5024 12.3438 15.0812 11.8562 15.5687L11.5907 15.8335C10.6061 16.8181 9.27066 17.3713 7.8782 17.3713C6.48574 17.3713 5.15031 16.8181 4.1657 15.8335C3.18108 14.8489 2.62793 13.5134 2.62793 12.121C2.62793 10.7285 3.18108 9.39311 4.1657 8.40849L5.22695 9.46974C4.87622 9.81741 4.59763 10.2309 4.40714 10.6865C4.21666 11.1422 4.11805 11.6309 4.11697 12.1248C4.11589 12.6186 4.21236 13.1078 4.40084 13.5642C4.58933 14.0207 4.86611 14.4354 5.21531 14.7846C5.56451 15.1338 5.97924 15.4106 6.43569 15.5991C6.89214 15.7876 7.38133 15.8841 7.87517 15.883C8.36901 15.8819 8.85777 15.7833 9.31339 15.5928C9.76902 15.4023 10.1825 15.1237 10.5302 14.773L10.7957 14.5075C11.4987 13.8043 11.8936 12.8506 11.8936 11.8562C11.8936 10.8619 11.4987 9.90822 10.7957 9.20499L9.73445 8.14374L10.7957 7.08324L10.7949 7.08249ZM15.8334 11.5907L14.7729 10.5302C15.1237 10.1826 15.4023 9.76906 15.5927 9.31344C15.7832 8.85781 15.8818 8.36905 15.8829 7.87521C15.884 7.38138 15.7875 6.89219 15.5991 6.43573C15.4106 5.97928 15.1338 5.56455 14.7846 5.21535C14.4354 4.86616 14.0207 4.58937 13.5642 4.40089C13.1077 4.2124 12.6186 4.11593 12.1247 4.11701C11.6309 4.11809 11.1421 4.21671 10.6865 4.40719C10.2309 4.59767 9.81736 4.87627 9.4697 5.22699L9.2042 5.49249C8.50118 6.19572 8.10625 7.14937 8.10625 8.14374C8.10625 9.13811 8.50118 10.0918 9.2042 10.795L10.2654 11.8562L9.2042 12.9167L8.1437 11.8562C7.65613 11.3687 7.26937 10.7899 7.0055 10.153C6.74163 9.51596 6.60582 8.83323 6.60582 8.14374C6.60582 7.45426 6.74163 6.77152 7.0055 6.13453C7.26937 5.49754 7.65613 4.91876 8.1437 4.43124L8.4092 4.16649C9.39381 3.18187 10.7292 2.62872 12.1217 2.62872C13.5142 2.62872 14.8496 3.18187 15.8342 4.16649C16.8188 5.15111 17.372 6.48653 17.372 7.87899C17.372 9.27145 16.8188 10.6069 15.8342 11.5915L15.8334 11.5907Z"
          fill="currentColor"
        ></path>
      </g>
    </svg>
  </Box>
);

interface AgentTypeDropdownProps {
  onAgentCreated?: () => void;
}

export const AgentTypeDropdown: React.FC<AgentTypeDropdownProps> = ({
  onAgentCreated,
}) => {
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const handleCreateSinglePromptAgent = async () => {
    try {
      // First create the LLM
      const llmParams = {
        model: DEFAULT_RETELL_TEXT_MODEL,
        start_speaker: "agent" as const,
        model_temperature: 0.7,
        general_prompt: "",
      };

      const createdLLM = await agentService.createRetellLLM(llmParams);

      // Then create the agent with the LLM
      await agentService.create({
        agent_name: "Single-Prompt Agent",
        response_engine: {
          type: "retell-llm",
          llm_id: createdLLM.llm_id,
        },
        voice_id: "11labs-Cimo",
        language: "en-US",
      });

      toast({
        title: "Agent created successfully",
        status: "success",
        duration: 3000,
      });

      // Call the callback if provided
      if (onAgentCreated) {
        onAgentCreated();
      }
    } catch (error: any) {
      console.error("Error creating agent:", error);
      toast({
        title: "Error creating agent",
        description:
          error.response?.data?.message ||
          "An error occurred while creating the agent",
        status: "error",
        duration: 5000,
      });
    }
  };

  // Placeholder functions for other agent types
  const handleCreateMultiPromptAgent = async () => {
    try {
      const llmParams = {
        model: DEFAULT_RETELL_TEXT_MODEL,
        start_speaker: "agent" as const,
        model_temperature: 0.7,
        general_prompt: "",
        states: [],
      };

      const createdLLM = await agentService.createRetellLLM(llmParams);

      await agentService.create({
        agent_name: "Multi-Prompt Agent",
        response_engine: {
          type: "retell-llm",
          llm_id: createdLLM.llm_id,
        },
        voice_id: "11labs-Cimo",
        language: "en-US",
      });

      toast({
        title: "Multi-Prompt Agent created successfully",
        status: "success",
        duration: 3000,
      });

      // Call the callback if provided
      if (onAgentCreated) {
        onAgentCreated();
      }
    } catch (error: any) {
      console.error("Error creating Multi-Prompt agent:", error);
      toast({
        title: "Error creating Multi-Prompt agent",
        description:
          error.response?.data?.message ||
          "An error occurred while creating the agent",
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleCreateCustomLLM = async () => {
    try {
      // Create the agent with custom-llm type
      await agentService.create({
        agent_name: "Custom LLM Agent",
        response_engine: {
          type: "custom-llm",
          llm_id: "custom", // This is a placeholder, not actually used
          llm_websocket_url: "https://replace-with-your-llm-url.com",
        },
        voice_id: "11labs-Cimo",
        language: "en-US",
      });

      toast({
        title: "Custom LLM Agent created successfully",
        status: "success",
        duration: 3000,
      });

      // Call the callback if provided
      if (onAgentCreated) {
        onAgentCreated();
      }
    } catch (error: any) {
      console.error("Error creating Custom LLM agent:", error);
      toast({
        title: "Error creating Custom LLM agent",
        description:
          error.response?.data?.message ||
          "An error occurred while creating the agent",
        status: "error",
        duration: 5000,
      });
    }
  };

  return (
    <Menu>
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon />}
        bg={isDark ? "gray.800" : "white"}
        color={isDark ? "white" : "black"}
        borderColor={isDark ? "gray.600" : "gray.200"}
        borderWidth="1px"
        _hover={{ bg: isDark ? "gray.700" : "gray.50" }}
      >
        Create an Agent
      </MenuButton>
      <MenuList
        bg={isDark ? "gray.800" : "white"}
        borderColor={isDark ? "gray.600" : "gray.200"}
      >
        {/* Single Prompt Agent */}
        <MenuItem
          onClick={handleCreateSinglePromptAgent}
          bg={isDark ? "gray.800" : "white"}
          _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
        >
          <Flex alignItems="center">
            <SinglePromptIcon isDark={isDark} />
            <Box>
              <Text fontWeight="medium" color={isDark ? "white" : "black"}>
                Single-Prompt Agent
              </Text>
              <Text fontSize="xs" color={isDark ? "gray.400" : "gray.500"}>
                For short calls and straightforward tasks
              </Text>
            </Box>
          </Flex>
        </MenuItem>

        {/* Multi-Prompt Agent */}
        <MenuItem
          onClick={handleCreateMultiPromptAgent}
          bg={isDark ? "gray.800" : "white"}
          _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
        >
          <Flex alignItems="center">
            <MultiPromptIcon isDark={isDark} />
            <Box>
              <Text fontWeight="medium" color={isDark ? "white" : "black"}>
                Multi-Prompt Agent
              </Text>
              <Text fontSize="xs" color={isDark ? "gray.400" : "gray.500"}>
                For lengthy calls and complex tasks
              </Text>
            </Box>
          </Flex>
        </MenuItem>

        {/* Custom LLM */}
        <MenuItem
          onClick={handleCreateCustomLLM}
          bg={isDark ? "gray.800" : "white"}
          _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
        >
          <Flex alignItems="center">
            <CustomLLMIcon isDark={isDark} />
            <Box>
              <Text fontWeight="medium" color={isDark ? "white" : "black"}>
                Custom LLM Agent
              </Text>
              <Text fontSize="xs" color={isDark ? "gray.400" : "gray.500"}>
                Attach your custom llm link
              </Text>
            </Box>
          </Flex>
        </MenuItem>
      </MenuList>
    </Menu>
  );
};
